import type {
  AdicionarPessoaEntrada,
  AlterarAtividadeEntrada,
  AlterarGruposEntrada,
  AlterarLayoutEntrada,
  CriarAtividadeEntrada,
  EditarProcessoEntrada,
  EnvioDiretoDto,
  EnvioDocumentoDto,
  LicitacaoDto,
  NotificacoesDto,
  UrlArquivoDto,
  CanaisDto,
  EditalDto,
  ArquivoEnviadoDto,
  ConviteParaAprovarDto,
  OpcoesConviteDto,
  ImportacaoDto,
  RespostaPerguntaDto,
  ResultadoImportacaoDto,
  BuscaDto,
  AceitarConviteEntrada,
  ConvidarEntrada,
  ConviteDto,
  ResultadoConviteDto,
  SolicitacaoPreenchidaDto,
  SolicitarAcessoEntrada,
  VinculoTelegramDto,
  EuDto,
  EsvaziarLixeiraEntrada,
  MoverProcessoEntrada,
  MudarEstadoEntrada,
  QuadroDto,
  RestaurarProcessoEntrada,
} from '@nexital/shared';
import { AppChat, CABECALHO_CLIENTE } from '@nexital/shared';
import {
  ErroApi,
  type AlvoLicitacao,
  type ApiEspacos,
  type ApiLicitacao,
  type ApiNotificacoes,
  type ApiAcessoPublico,
  type ApiQuadro,
  type FonteToken,
  type ProcessoGravado,
} from '../application/portas';
import { enviarDiretoAoS3 } from './envio-s3';

type Metodo = 'GET' | 'PATCH' | 'POST' | 'PUT' | 'DELETE';

const caminhoLicitacao = ({ quadroUuid, processoUuid }: AlvoLicitacao) => `/api/quadros/${quadroUuid}/processos/${processoUuid}`;

/**
 * Cliente HTTP da API com o JWT em `Authorization: Bearer` (RQ-ACE-010). Em 401, pede uma renovação
 * e tenta de novo uma vez; se não der, a sessão expira e a tela volta ao login.
 */
export class ApiHttp implements ApiQuadro, ApiEspacos, ApiLicitacao, ApiNotificacoes, ApiAcessoPublico {
  constructor(
    private readonly sessao: FonteToken,
    /** Id desta aba: o tempo real usa para a aba não reagir às próprias gravações. */
    private readonly clienteId: string,
  ) {}

  /** FormData vai como multipart (o navegador define o content-type com a fronteira); o resto, como JSON. */
  private enviar(metodo: Metodo, caminho: string, corpo?: unknown) {
    const token = this.sessao.token;
    const formulario = corpo instanceof FormData;
    const headers: Record<string, string> = { [CABECALHO_CLIENTE]: this.clienteId };
    if (token) headers.authorization = `Bearer ${token}`;
    if (!formulario && corpo !== undefined) headers['content-type'] = 'application/json';
    return fetch(caminho, {
      method: metodo,
      headers,
      body: formulario || corpo === undefined ? (corpo as FormData | undefined) : JSON.stringify(corpo),
    });
  }

  /** Chamada autenticada à API (também usada pelo cliente das notas). */
  async chamar<T>(metodo: Metodo, caminho: string, corpo?: unknown): Promise<T> {
    let resposta = await this.enviar(metodo, caminho, corpo);
    if (resposta.status === 401 && (await this.sessao.renovar())) resposta = await this.enviar(metodo, caminho, corpo);
    if (resposta.status === 401) this.sessao.expirou();
    const json = await resposta.json().catch(() => ({}));
    if (!resposta.ok) throw new ErroApi(resposta.status, json.mensagem ?? `Erro ${resposta.status}`, json.avisos);
    return json as T;
  }

  obterLicitacao(alvo: AlvoLicitacao) {
    return this.chamar<LicitacaoDto>('GET', caminhoLicitacao(alvo));
  }

  editarProcesso(alvo: AlvoLicitacao, entrada: EditarProcessoEntrada) {
    return this.chamar<{ versao: number }>('PATCH', caminhoLicitacao(alvo), entrada);
  }

  criarAtividade(alvo: AlvoLicitacao, entrada: CriarAtividadeEntrada) {
    return this.chamar<{ uuid: string; versao: number }>('POST', `${caminhoLicitacao(alvo)}/atividades`, entrada);
  }

  alterarAtividade(alvo: AlvoLicitacao, atividadeUuid: string, entrada: AlterarAtividadeEntrada) {
    return this.chamar<{ versao: number }>('PATCH', `${caminhoLicitacao(alvo)}/atividades/${atividadeUuid}`, entrada);
  }

  adicionarPessoa(alvo: AlvoLicitacao, entrada: AdicionarPessoaEntrada) {
    return this.chamar<{ uuid: string }>('POST', `${caminhoLicitacao(alvo)}/pessoas`, entrada);
  }

  async removerPessoa(alvo: AlvoLicitacao, pessoaUuid: string) {
    await this.chamar('DELETE', `${caminhoLicitacao(alvo)}/pessoas/${pessoaUuid}`);
  }

  alterarLayout(alvo: AlvoLicitacao, entrada: AlterarLayoutEntrada) {
    return this.chamar<{ versoes: Record<number, number> }>('PUT', `${caminhoLicitacao(alvo)}/layout`, entrada);
  }

  enviarDocumento(alvo: AlvoLicitacao, arquivo: File) {
    const formulario = new FormData();
    formulario.append('arquivo', arquivo, arquivo.name);
    return this.chamar<EnvioDocumentoDto>('POST', `${caminhoLicitacao(alvo)}/documentos`, formulario);
  }

  urlDocumento(alvo: AlvoLicitacao, documentoUuid: string) {
    return this.chamar<UrlArquivoDto>('GET', `${caminhoLicitacao(alvo)}/documentos/${documentoUuid}/arquivo`);
  }

  async obterEdital(alvo: AlvoLicitacao) {
    try {
      return await this.chamar<EditalDto>('GET', `${caminhoLicitacao(alvo)}/edital`);
    } catch (e) {
      if (e instanceof ErroApi && e.status === 404) return null;
      throw e;
    }
  }

  perguntar(alvo: AlvoLicitacao, pergunta: string) {
    return this.chamar<RespostaPerguntaDto>('POST', `${caminhoLicitacao(alvo)}/perguntas`, { pergunta });
  }

  importar(quadroUuid: string, arquivos: File[], processoUuid?: string) {
    const formulario = new FormData();
    for (const a of arquivos) formulario.append('arquivos', a, a.name);
    const destino = processoUuid ? `?processo=${encodeURIComponent(processoUuid)}` : '';
    return this.chamar<ResultadoImportacaoDto>('POST', `/api/quadros/${quadroUuid}/importacoes${destino}`, formulario);
  }

  arquivosSemProcesso(quadroUuid: string) {
    return this.chamar<ArquivoEnviadoDto[]>('GET', `/api/quadros/${quadroUuid}/arquivos-sem-processo`);
  }

  async acaoArquivo(quadroUuid: string, arquivoUuid: string, acao: string, processoUuid?: string) {
    await this.chamar('POST', `/api/quadros/${quadroUuid}/arquivos-sem-processo/${arquivoUuid}/${acao}`, processoUuid ? { processoUuid } : {});
  }

  importacoes(quadroUuid: string) {
    return this.chamar<ImportacaoDto[]>('GET', `/api/quadros/${quadroUuid}/importacoes`);
  }

  obterNotificacoes() {
    return this.chamar<NotificacoesDto>('GET', '/api/eu/notificacoes');
  }

  async marcarNotificacoesLidas(uuids?: string[]) {
    await this.chamar('POST', '/api/eu/notificacoes/lidas', uuids ? { uuids } : {});
  }

  /** Foto de perfil direto ao S3 (RQ-ARQ-003): pede a URL, envia e confirma. Devolve a URL pública. */
  async enviarFoto(arquivo: File) {
    const referencia = await enviarDiretoAoS3(arquivo, (entrada) => this.chamar<EnvioDiretoDto>('POST', '/api/eu/avatar/envio', entrada));
    const { avatarUrl } = await this.chamar<{ avatarUrl: string }>('POST', '/api/eu/avatar/confirmar', { referencia });
    return avatarUrl;
  }

  obterEu() {
    return this.chamar<EuDto>('GET', '/api/eu');
  }

  consultarConvite(token: string) {
    return this.chamar<ConviteDto>('GET', `/api/convites/${encodeURIComponent(token)}`);
  }

  async aceitarConvite(token: string, dados: AceitarConviteEntrada) {
    await this.chamar('POST', `/api/convites/${encodeURIComponent(token)}/aceitar`, dados);
  }

  solicitacaoPreenchida(token: string) {
    return this.chamar<SolicitacaoPreenchidaDto>('GET', `/api/acesso/solicitacao?t=${encodeURIComponent(token)}`);
  }

  async solicitarAcesso(dados: SolicitarAcessoEntrada) {
    await this.chamar('POST', '/api/acesso/solicitacoes', dados);
  }

  convidar(quadroUuid: string, dados: ConvidarEntrada) {
    return this.chamar<ResultadoConviteDto>('POST', `/api/quadros/${quadroUuid}/convites`, dados);
  }

  opcoesConvite(quadroUuid: string) {
    return this.chamar<OpcoesConviteDto>('GET', `/api/quadros/${quadroUuid}/convites/opcoes`);
  }

  conviteParaAprovar(quadroUuid: string, uuid: string) {
    return this.chamar<ConviteParaAprovarDto>('GET', `/api/quadros/${quadroUuid}/convites/${uuid}`);
  }

  async decidirConvite(quadroUuid: string, uuid: string, decisao: 'aprovar' | 'recusar', motivo?: string) {
    await this.chamar('POST', `/api/quadros/${quadroUuid}/convites/${uuid}/${decisao}`, motivo ? { motivo } : {});
  }

  buscar(quadroUuid: string, termo: string, processoUuid?: string) {
    const params = new URLSearchParams({ q: termo });
    if (processoUuid) params.set('processo', processoUuid);
    return this.chamar<BuscaDto>('GET', `/api/quadros/${quadroUuid}/busca?${params}`);
  }

  obterCanais() {
    return this.chamar<CanaisDto>('GET', '/api/eu/canais');
  }

  vincularTelegram() {
    return this.chamar<VinculoTelegramDto>('POST', '/api/eu/telegram/vinculo', {});
  }

  async desvincularTelegram() {
    await this.chamar('DELETE', `/api/eu/canais/${AppChat.Telegram}`);
  }

  obterQuadro(quadroUuid: string) {
    return this.chamar<QuadroDto>('GET', `/api/quadros/${quadroUuid}`);
  }

  moverProcesso(quadroUuid: string, processoUuid: string, entrada: MoverProcessoEntrada) {
    return this.chamar<ProcessoGravado>('PATCH', `/api/quadros/${quadroUuid}/processos/${processoUuid}/posicao`, entrada);
  }

  alterarGrupos(quadroUuid: string, entrada: AlterarGruposEntrada) {
    return this.chamar<{ versoes: Record<string, number> }>('PATCH', `/api/quadros/${quadroUuid}/grupos`, entrada);
  }

  mudarEstado(quadroUuid: string, processoUuid: string, entrada: MudarEstadoEntrada) {
    return this.chamar<ProcessoGravado>('PATCH', `/api/quadros/${quadroUuid}/processos/${processoUuid}/estado`, entrada);
  }

  restaurarProcesso(quadroUuid: string, processoUuid: string, entrada: RestaurarProcessoEntrada) {
    const caminho = `/api/quadros/${quadroUuid}/processos/${processoUuid}/restaurar`;
    return this.chamar<{ versao: number; estadoUuid: string }>('POST', caminho, entrada);
  }

  esvaziarLixeira(quadroUuid: string, entrada: EsvaziarLixeiraEntrada) {
    return this.chamar<{ excluidos: number }>('POST', `/api/quadros/${quadroUuid}/lixeira/esvaziar`, entrada);
  }
}
